import { getScroll } from "./scroll";
import splide from "@splidejs/splide";
import "@splidejs/splide/css";

// Function to calculate the center of the modal
const calculateModalCenterScreenPosition = (modal) => {
	// if the document doesn't have the class .has-scroll-smooth, we don't need to adjust the modal position, so return
	if (!document.documentElement.classList.contains("has-scroll-smooth")) return;
	const modalBox = modal.querySelector(".modal-box");

	// The translateY value should bring the modal to the middle of the viewport.
	// Since the modal is already centered via CSS with translate(-50%, -50%),
	// we only need to consider the vertical alignment relative to the scroll position.
	const scroll = getScroll();
	const viewportHeight = window.innerHeight;
	const scrollY = scroll.scroll.instance.scroll.y;

	// Adjust the modal's transform to account for the current scroll position
	// We don't need to adjust by the modal's height since CSS handles vertical centering
	const translateYValue = viewportHeight / 2 + scrollY - 100;

	modal.style.transform = `translate(-50%, -50%) translateY(${translateYValue}px)`;
};

// Unified function to open any modal and center it
const openModal = (modal) => {
	modal.classList.remove("close");
	modal.classList.add("fade-in");
	setTimeout(() => {
		modal.classList.remove("fade-in");
		modal.classList.add("open");
		document.body.classList.add("modal-open");
		calculateModalCenterScreenPosition(modal); // Center it in LocomotiveScroll's virtual viewport
	}, 500);

	scroll.stop();

	// Close modal on click outside content
	modal.addEventListener("click", (e) => {
		if (e.target.classList.contains("modal") || e.target.classList.contains("close")) {
			closeModal(modal);
		}
	});
};

// Unified function to close any modal and restart scroll
const closeModal = (modal) => {
	modal.classList.remove("open");
	modal.classList.add("fade-out");
	stopVideo(modal);
	setTimeout(() => {
		modal.classList.remove("fade-out");
		modal.classList.add("close");
		document.body.classList.remove("modal-open");
	}, 250);
	scroll.start();
};

const stopVideo = (modal) => {
	const videoIframe = modal.querySelector("iframe");
	if (!videoIframe) return;
	const videoSrc = videoIframe.src;
	videoIframe.src = videoSrc;
};

// Initialize YouTube modals with LocomotiveScroll adjustment
const initYouTubeModals = () => {
	const modal = document.getElementById("youtubeModal");
	if (!modal) return;
	const videoIframe = modal.querySelector("#youtubeVideo");
	const closeModalBtn = modal.querySelector(".close");

	document.querySelectorAll(".youtube-thumbnail").forEach((thumbnail) => {
		thumbnail.addEventListener("click", function (e) {
			e.preventDefault();
			const videoId = this.getAttribute("data-video-id");
			const videoType = this.getAttribute("data-type");

			// Handle video or playlist
			let videoSrc =
				videoType === "playlist"
					? `https://www.youtube.com/embed/videoseries?list=${videoId}&autoplay=1`
					: `https://www.youtube.com/embed/${videoId}?autoplay=1`;

			videoIframe.src = videoSrc;
			openModal(modal);
		});
	});

	closeModalBtn.addEventListener("click", function () {
		closeModal(modal);
		videoIframe.src = "";
	});
};

const initPhotoGalleryModals = () => {
	if (!document.querySelector("#photoGalleryModal")) return;
	const modal = document.getElementById("photoGalleryModal");
	const modalContent = modal.querySelector(".modal-content");
	const closeModalBtn = modal.querySelector(".close");

	document.querySelectorAll(".photo-gallery-thumbnail").forEach((thumbnail) => {
		thumbnail.addEventListener("click", function (e) {
			e.preventDefault();

			modalContent.innerHTML = ""; // Clear the modal content

			// Create the main slider container
			const mainSlider = document.createElement("div");
			mainSlider.id = "main-slider";
			mainSlider.classList.add("splide");
			const mainSliderTrack = document.createElement("div");
			mainSliderTrack.classList.add("splide__track");
			const mainSliderList = document.createElement("ul");
			mainSliderList.classList.add("splide__list");

			// Create the thumbnail slider container
			const thumbnailSlider = document.createElement("div");
			thumbnailSlider.id = "thumbnail-slider";
			thumbnailSlider.classList.add("splide");
			const thumbnailSliderTrack = document.createElement("div");
			thumbnailSliderTrack.classList.add("splide__track");
			const thumbnailSliderList = document.createElement("ul");
			thumbnailSliderList.classList.add("splide__list");

			// Add the images from the gallery-container to both sliders
			const galleryContainer = thumbnail.querySelector(".gallery-container");
			const images = galleryContainer.querySelectorAll(".gallery-image");

			images.forEach((image, index) => {
				const imageUrl = image.style.backgroundImage.replace('url("', "").replace('")', "");

				// Create main image slide
				const mainSlide = document.createElement("li");
				mainSlide.classList.add("splide__slide");
				const imgMain = document.createElement("img");
				imgMain.src = imageUrl;
				mainSlide.appendChild(imgMain);
				mainSliderList.appendChild(mainSlide);

				// Create thumbnail slide
				const thumbSlide = document.createElement("li");
				thumbSlide.classList.add("splide__slide");
				const imgThumb = document.createElement("img");
				imgThumb.src = imageUrl;
				thumbSlide.appendChild(imgThumb);
				thumbnailSliderList.appendChild(thumbSlide);
			});

			// Append the main slider track and list
			mainSliderTrack.appendChild(mainSliderList);
			mainSlider.appendChild(mainSliderTrack);

			// Append the thumbnail slider track and list
			thumbnailSliderTrack.appendChild(thumbnailSliderList);
			thumbnailSlider.appendChild(thumbnailSliderTrack);

			// Append the sliders to the modal content
			modalContent.appendChild(mainSlider);
			modalContent.appendChild(thumbnailSlider);

			// Initialize SplideJS for both sliders
			const mainSplide = new splide("#main-slider", {
				type: "fade",
				heightRatio: 0.5,
				pagination: false,
				arrows: false,
				cover: true,
			}).mount();

			const thumbnailSplide = new splide("#thumbnail-slider", {
				rewind: true,
				fixedWidth: 104,
				fixedHeight: 58,
				isNavigation: true,
				gap: 10,
				focus: "center",
				pagination: false,
				cover: true,
				dragMinThreshold: {
					mouse: 4,
					touch: 10,
				},
				breakpoints: {
					640: {
						fixedWidth: 66,
						fixedHeight: 38,
					},
				},
			}).mount();

			// Sync the sliders
			mainSplide.sync(thumbnailSplide);

			// Open the modal
			openModal(modal);
		});
	});

	// Close modal
	closeModalBtn.addEventListener("click", function () {
		closeModal(modal);
		modalContent.innerHTML = ""; // Clear modal content when closed
	});
};

// Initialize team member modals with LocomotiveScroll adjustment
const initTeamModals = () => {
	if (!document.querySelector("#teamMemberModal")) return;
	const modal = document.getElementById("teamMemberModal");
	const modalContent = modal.querySelector(".modal-content");
	const closeModalBtn = modal.querySelector(".close");

	document.querySelectorAll(".team-thumb").forEach((thumbnail) => {
		thumbnail.addEventListener("click", function (e) {
			e.preventDefault();

			modalContent.innerHTML = "";
			const bioDiv = document.createElement("div");
			bioDiv.classList.add("bio");

			const headshot = thumbnail.querySelector(".headshot").cloneNode(true);
			bioDiv.appendChild(headshot);

			const wedge = thumbnail.querySelector(".wedge");
			const wedgeClone = wedge.cloneNode(true);
			bioDiv.appendChild(wedgeClone);

			const details = thumbnail.querySelector(".details").cloneNode(true);
			bioDiv.appendChild(details);

			modalContent.appendChild(bioDiv);
			openModal(modal);
		});
	});

	closeModalBtn.addEventListener("click", function () {
		closeModal(modal);
		modalContent.innerHTML = "";
	});
};

// Initialize all modals
export const initModals = () => {
	initYouTubeModals();
	initPhotoGalleryModals();
	initTeamModals();
};
